/**
 * Artifact renderer with template delegation
 *
 * Handles common loading/error states and delegates artifact-specific
 * rendering to specialized templates based on artifact kind.
 *
 * Data Contract: The gatherer's select function guarantees that data will be Artifact | null.
 */

import { ERROR, LOADING, OK } from "helpers/data-statuses"
import { Button } from "@mui/material"
import { getArtifactTemplate } from "./templates"
import type { ArtifactRendererProps } from "connected-components/dashboard/modules/ai-artifact/types"
import { LoadingArtifactIndicator } from "./loading-artifact-indicator"
import "./style.scss"

/**
 * Artifact renderer component
 *
 * Provides common wrapper for loading/error states and delegates
 * to kind-specific templates for actual artifact rendering.
 *
 */
export default function ArtifactRenderer({
  status,
  data,
  title,
  onRetry,
  isPinning,
  ...rendererProps
}: ArtifactRendererProps) {
  const normalizedTitle = title?.trim() || "module"
  const effectiveStatus = isPinning ? LOADING : status

  const loadingMessage = `Loading ${normalizedTitle}...`
  const errorMessage = `Unable to load ${normalizedTitle}.`
  const emptyMessage = `No ${normalizedTitle} available.`

  if (effectiveStatus === LOADING) {
    return <LoadingArtifactIndicator message={loadingMessage} />
  }

  if (effectiveStatus === ERROR) {
    return (
      <div className="ai-artifact-module__state ai-artifact-module__error">
        <div className="ai-artifact-module__content">
          <p>{errorMessage}</p>
          {onRetry && (
            <Button onClick={onRetry} type="button" variant="contained" color="primary">
              Retry
            </Button>
          )}
        </div>
      </div>
    )
  }

  if (effectiveStatus !== OK) {
    return null
  }

  // Check if we have artifact data
  if (!data) {
    return (
      <div className="ai-artifact-module__state ai-artifact-module__empty">
        <div className="ai-artifact-module__content">
          <p>{emptyMessage}</p>
        </div>
      </div>
    )
  }

  // Get template for this artifact type
  const template = getArtifactTemplate(data.type)
  if (!template) {
    return null
  }

  // Render using the appropriate template
  const { Component } = template
  return <Component artifact={data} {...rendererProps} />
}
